import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Badge } from './ui/Badge';
import { MOCK_USERS, LEAD_SOURCES, INITIAL_CDS_TIERS } from '../data/mockData';
import { useUserAdjustments } from '../data/feesStore';
import { formatCurrency, cn } from '../lib/utils';
import { DollarSign, FileText, Wallet, Calendar, ChevronDown, Receipt } from 'lucide-react';
export function LoanOfficerView() {
  // Impersonation state: default to the first BPS user for demonstration
  const [selectedUserId, setSelectedUserId] = useState<string>(MOCK_USERS.find(u => u.model === 'BPS')?.id || MOCK_USERS[0].id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const user = useMemo(() => MOCK_USERS.find(u => u.id === selectedUserId)!, [selectedUserId]);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(user.monthlyLoans[0].key);
  // Reset the selected month to the current month when switching loan officers
  useEffect(() => {
    setSelectedMonthKey(user.monthlyLoans[0].key);
  }, [selectedUserId, user.monthlyLoans]);
  const selectedMonth = user.monthlyLoans.find(m => m.key === selectedMonthKey) || user.monthlyLoans[0];
  const activeLoans = selectedMonth.loans;
  const activeTotal = activeLoans.reduce((sum, loan) => sum + loan.commissionAmount, 0);
  const adjustments = useUserAdjustments(selectedUserId);
  const totalAdjustments = adjustments.reduce((sum, adj) => sum + adj.amount, 0);
  // Calculate financials
  const financials = useMemo(() => {
    const totalCommission = user.loans.reduce((sum, loan) => sum + loan.commissionAmount, 0);
    const netPay = totalCommission - user.drawBalance - user.currentMonthDraw - totalAdjustments;
    // Calculate last month's commission from actual previous loans data
    const lastMonthCommission = user.previousLoans.reduce((sum, loan) => sum + loan.commissionAmount, 0);
    return {
      totalCommission,
      lastMonthCommission,
      netPay
    };
  }, [user, totalAdjustments]);
  const currentTier = user.model === 'CDS' ? INITIAL_CDS_TIERS.find(t => user.fundedMTD >= t.min && (t.max === null || user.fundedMTD <= t.max)) : null;
  return <div className="space-y-6" data-id="element-298">
      <div className="flex items-center justify-between pb-2" data-id="element-299">
        <div data-id="element-300">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-301">
            My Dashboard
          </h1>
          <p className="text-sm text-slate-500" data-id="element-302">Viewing as Loan Officer</p>
        </div>
      </div>

      {/* Impersonation Header */}
      <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-slate-200/60" data-id="element-303">
        <div className="flex items-center gap-3" data-id="element-304">
          <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-305" />
          <div data-id="element-306">
            <div className="text-sm font-semibold text-slate-900" data-id="element-307">
              {user.name}
            </div>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-308">
              <Badge variant={user.model === 'BPS' ? 'default' : 'secondary'} className={cn('text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md', user.model === 'BPS' ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-100')} data-id="element-309">
                {user.model}
              </Badge>
              <span className="text-xs text-slate-500 font-medium" data-id="element-310">
                {user.title}
              </span>
            </div>
          </div>
        </div>

        <div className="relative" data-id="element-311">
          <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors shadow-sm" data-id="element-312">
            <span className="text-sm font-medium text-slate-600" data-id="element-313">
              Switch LO
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-314" />
          </button>

          {isDropdownOpen && <div className="absolute right-0 mt-2 w-64 max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-50" data-id="element-315">
              <div className="p-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-100" data-id="element-316">
                Impersonate User
              </div>
              {MOCK_USERS.map(u => <button key={u.id} onClick={() => {
            setSelectedUserId(u.id);
            setIsDropdownOpen(false);
          }} className={cn('w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-0', selectedUserId === u.id && 'bg-blue-50/50')} data-id="element-317">
                  <img src={u.avatar} alt={u.name} className="w-8 h-8 rounded-full bg-slate-200" data-id="element-318" />
                  <div data-id="element-319">
                    <div className="text-sm font-medium text-slate-900" data-id="element-320">
                      {u.name}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-321">{u.model}</div>
                  </div>
                </button>)}
            </div>}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-322">
        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-323">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-324">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-325">
              Current Commission
            </CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-600" data-id="element-326" />
          </CardHeader>
          <CardContent data-id="element-327">
            <div className="text-2xl font-bold text-slate-900" data-id="element-328">
              {formatCurrency(financials.totalCommission)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-329">
              vs {formatCurrency(financials.lastMonthCommission)} last month
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-330">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-331">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-332">
              Funded Loans
            </CardTitle>
            <FileText className="h-4 w-4 text-blue-600" data-id="element-333" />
          </CardHeader>
          <CardContent data-id="element-334">
            <div className="text-2xl font-bold text-slate-900" data-id="element-335">
              {user.fundedMTD}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-336">
              vs {user.fundedLastMonth} last month
            </p>
          </CardContent>
        </Card>

        {user.drawBalance + user.currentMonthDraw > 0 && <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-337">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-338">
              <CardTitle className="text-sm font-medium text-slate-600" data-id="element-339">
                Draw Balance
              </CardTitle>
              <Wallet className="h-4 w-4 text-red-600" data-id="element-340" />
            </CardHeader>
            <CardContent data-id="element-341">
              <div className="text-2xl font-bold text-slate-900" data-id="element-342">
                {formatCurrency(user.drawBalance + user.currentMonthDraw)}
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-343">
                To be repaid from commissions
              </p>
            </CardContent>
          </Card>}

        {totalAdjustments > 0 && <Card className="shadow-sm border-slate-200/60 rounded-xl" data-id="element-344">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-345">
              <CardTitle className="text-sm font-medium text-slate-600" data-id="element-346">
                Fees & Recoupments
              </CardTitle>
              <Receipt className="h-4 w-4 text-red-500" data-id="element-347" />
            </CardHeader>
            <CardContent data-id="element-348">
              <div className="text-2xl font-bold text-slate-900" data-id="element-349">
                {formatCurrency(totalAdjustments)}
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-350">
                Total manual deductions
              </p>
            </CardContent>
          </Card>}

        <Card className={cn('shadow-sm border-slate-200/60 rounded-xl', financials.netPay > 0 ? 'bg-emerald-50/30' : '')} data-id="element-351">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-352">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-353">
              Estimated Net Pay
            </CardTitle>
            <DollarSign className={cn('h-4 w-4', financials.netPay > 0 ? 'text-emerald-600' : 'text-slate-400')} data-id="element-354" />
          </CardHeader>
          <CardContent data-id="element-355">
            <div className={cn('text-2xl font-bold', financials.netPay > 0 ? 'text-emerald-700' : 'text-slate-900')} data-id="element-356">
              {formatCurrency(Math.max(0, financials.netPay))}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-357">After draw repayments</p>
          </CardContent>
        </Card>
      </div>

      {adjustments.length > 0 && <Card className="shadow-sm border-slate-200/60 bg-slate-50/50 rounded-xl" data-id="element-358">
          <CardHeader className="pb-3" data-id="element-359">
            <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2" data-id="element-360">
              <Receipt className="w-4 h-4 text-slate-500" data-id="element-361" />
              Manual Adjustments
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-362">
            <div className="space-y-3" data-id="element-363">
              {adjustments.map(adj => <div key={adj.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm" data-id="element-364">
                  <div data-id="element-365">
                    <div className="flex items-center gap-2" data-id="element-366">
                      <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider font-bold', adj.type === 'fee' ? 'text-slate-600 border-slate-200 bg-slate-100' : 'text-red-600 border-red-200 bg-red-50')} data-id="element-367">
                        {adj.type}
                      </Badge>
                      <span className="font-medium text-slate-900" data-id="element-368">
                        {adj.label}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-2" data-id="element-369">
                      <span data-id="element-370">
                        {new Date(adj.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </span>
                      {adj.loanLabel && <>
                          <span className="text-slate-300" data-id="element-371">•</span>
                          <span data-id="element-372">Loan: {adj.loanLabel}</span>
                        </>}
                    </div>
                  </div>
                  <div className="font-bold text-red-600" data-id="element-373">
                    -{formatCurrency(adj.amount)}
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-374">
        {/* Left Column: Rate Table or Tier Info */}
        <div className="lg:col-span-1 space-y-6" data-id="element-375">
          <Card className="shadow-sm border-slate-200/60 h-full flex flex-col rounded-xl" data-id="element-376">
            <CardHeader className="border-b border-slate-100 bg-white rounded-t-xl pb-4" data-id="element-377">
              <CardTitle data-id="element-378">My Pay Plan</CardTitle>
              <CardDescription data-id="element-379">
                {user.model === 'BPS' ? 'Your basis points by lead source' : 'Your current CDS tier status'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0 bg-slate-50/30" data-id="element-380">
              {user.model === 'BPS' ? <Table data-id="element-381">
                  <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-382">
                    <TableRow className="hover:bg-transparent" data-id="element-383">
                      <TableHead className="font-semibold text-slate-600 pl-6" data-id="element-384">
                        Lead Source
                      </TableHead>
                      <TableHead className="text-right font-semibold text-slate-600 pr-6" data-id="element-385">
                        Rate (BPS)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-386">
                    {LEAD_SOURCES.map(source => <TableRow key={source} className="hover:bg-white transition-colors" data-id="element-387">
                        <TableCell className="font-medium text-slate-700 pl-6" data-id="element-388">
                          {source}
                        </TableCell>
                        <TableCell className="text-right pr-6" data-id="element-389">
                          <Badge variant="secondary" className="font-bold bg-slate-100 text-slate-700" data-id="element-390">
                            {user.rates?.[source] || 0}
                          </Badge>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table> : <div className="p-6 space-y-6" data-id="element-391">
                  <div className="text-center p-6 bg-white rounded-xl border border-blue-100 shadow-sm" data-id="element-392">
                    <div className="text-sm font-medium text-blue-600 mb-2" data-id="element-393">
                      Current Tier
                    </div>
                    <div className="text-4xl font-bold text-slate-900 mb-1" data-id="element-394">
                      {currentTier ? formatCurrency(currentTier.amount) : '$0'}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-395">
                      per funded loan
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-396">
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider" data-id="element-397">
                      Tier Ladder
                    </h4>
                    {INITIAL_CDS_TIERS.map(tier => {
                  const isActive = currentTier?.id === tier.id;
                  return <div key={tier.id} className={cn('flex items-center justify-between p-3 rounded-lg border', isActive ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' : 'bg-white border-slate-100')} data-id="element-398">
                          <div className={cn('font-medium', isActive ? 'text-blue-900' : 'text-slate-600')} data-id="element-399">
                            {tier.min} {tier.max ? `- ${tier.max}` : '+'} Loans
                          </div>
                          <div className={cn('font-bold', isActive ? 'text-blue-700' : 'text-slate-900')} data-id="element-400">
                            {formatCurrency(tier.amount)}
                          </div>
                        </div>;
                })}
                  </div>
                </div>}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Loan Details Table */}
        <div className="lg:col-span-2" data-id="element-401">
          <Card className="shadow-sm border-slate-200/60 h-full flex flex-col rounded-xl" data-id="element-402">
            <CardHeader className="border-b border-slate-100 bg-white rounded-t-xl pb-4 flex flex-row items-start justify-between" data-id="element-403">
              <div data-id="element-404">
                <CardTitle data-id="element-405">Funded Loans</CardTitle>
                <CardDescription data-id="element-406">
                  Commission breakdown for {selectedMonth.label}
                </CardDescription>
              </div>
              <div className="relative shrink-0" data-id="element-407">
                <label htmlFor="lo-month-select" className="sr-only" data-id="element-408">
                  Select month
                </label>
                <select id="lo-month-select" value={selectedMonthKey} onChange={e => setSelectedMonthKey(e.target.value)} className="appearance-none h-9 rounded-lg border border-slate-200 bg-white pl-3 pr-9 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer" data-id="element-409">
                  {user.monthlyLoans.map(m => <option key={m.key} value={m.key} data-id="element-410">
                      {m.label}
                    </option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-411" />
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-412">
              <Table data-id="element-413">
                <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-414">
                  <TableRow className="hover:bg-transparent" data-id="element-415">
                    <TableHead className="font-semibold text-slate-600 pl-6" data-id="element-416">
                      Borrower
                    </TableHead>
                    <TableHead className="font-semibold text-slate-600" data-id="element-417">
                      Date
                    </TableHead>
                    <TableHead className="font-semibold text-slate-600" data-id="element-418">
                      Lead Source
                    </TableHead>
                    <TableHead className="text-right font-semibold text-slate-600" data-id="element-419">
                      BPS
                    </TableHead>
                    <TableHead className="text-right font-semibold text-slate-600" data-id="element-420">
                      Loan Amount
                    </TableHead>
                    <TableHead className="text-right font-semibold text-slate-600 pr-6" data-id="element-421">
                      Commission
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-422">
                  {activeLoans.map(loan => <TableRow key={loan.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-423">
                      <TableCell className="font-medium text-slate-900 pl-6" data-id="element-424">
                        {loan.borrowerName}
                      </TableCell>
                      <TableCell className="text-slate-500" data-id="element-425">
                        <div className="flex items-center" data-id="element-426">
                          <Calendar className="w-3 h-3 mr-1.5 text-slate-400" data-id="element-427" />
                          {new Date(loan.fundedDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                        </div>
                      </TableCell>
                      <TableCell data-id="element-428">
                        <Badge variant="outline" className="text-slate-600 font-medium" data-id="element-429">
                          {loan.leadSource}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-slate-600" data-id="element-430">
                        {user.model === 'BPS' ? <span className="font-medium text-slate-700" data-id="element-431">
                            {user.rates?.[loan.leadSource] ?? 0} bps
                          </span> : <span className="text-slate-400" data-id="element-432">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-slate-600" data-id="element-433">
                        {formatCurrency(loan.loanAmount)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700 pr-6" data-id="element-434">
                        {formatCurrency(loan.commissionAmount)}
                      </TableCell>
                    </TableRow>)}
                  {activeLoans.length === 0 && <TableRow data-id="element-435">
                      <TableCell colSpan={6} className="h-32 text-center text-slate-500" data-id="element-436">
                        No funded loans for {selectedMonth.label}.
                      </TableCell>
                    </TableRow>}
                  {activeLoans.length > 0 && <TableRow className="bg-slate-50/80 hover:bg-slate-50/80 border-t-2 border-slate-200" data-id="element-437">
                      <TableCell colSpan={5} className="pl-6 font-semibold text-slate-700" data-id="element-438">
                        Total Commission ({activeLoans.length} loans)
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700 pr-6" data-id="element-439">
                        {formatCurrency(activeTotal)}
                      </TableCell>
                    </TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}